<template>
  <!-- Баннер пропозиції встановити PWA на головний екран -->
  <Transition name="slide-up">
    <div v-if="showPrompt" class="pwa-install-prompt" :class="{ 'pwa-install-prompt--ios': isIOSUA }">
      <div class="pwa-install-content">
        <div class="pwa-install-icon">
          <span class="material-symbols-outlined">install_mobile</span>
        </div>
        <div class="pwa-install-text">
          <!-- Варіант 1: iOS не-Safari — просимо відкрити в Safari -->
          <template v-if="isIOSUA && !isSafari">
            <p class="pwa-install-title">{{ t('pwa.install.openInSafariTitle') }}</p>
            <p class="pwa-install-description">{{ t('pwa.install.openInSafariDesc') }}</p>
          </template>
          <!-- Варіант 2: iOS Safari — пропонуємо подивитись гайд -->
          <template v-else-if="isIOSUA && isSafari">
            <p class="pwa-install-title">{{ t('pwa.install.title') }}</p>
            <p class="pwa-install-description">{{ t('pwa.install.iosShort') }}</p>
          </template>
          <!-- Варіант 3: Samsung Internet — його WebAPK ріже Play Protect, ведемо в Chrome -->
          <template v-else-if="isSamsungUA">
            <p class="pwa-install-title">{{ t('pwa.install.openInChromeTitle') }}</p>
            <p class="pwa-install-description">{{ t('pwa.install.openInChromeDesc') }}</p>
          </template>
          <!-- Варіант 4: Android/Desktop -->
          <template v-else>
            <p class="pwa-install-title">{{ t('pwa.install.title') }}</p>
            <p class="pwa-install-description">{{ t('pwa.install.description') }}</p>
          </template>
        </div>
        <div class="pwa-install-actions">
          <!-- iOS Safari: кнопка "Показати інструкцію" -->
          <button v-if="isIOSUA && isSafari" class="pwa-btn pwa-btn-primary" @click="showGuideAndDismissSoft">
            {{ t('pwa.install.showGuide') }}
          </button>
          <!-- iOS не-Safari: "Копіювати посилання" -->
          <button v-if="isIOSUA && !isSafari" class="pwa-btn pwa-btn-primary" @click="copyUrl">
            {{ copied ? t('pwa.install.copied') : t('pwa.install.copyUrl') }}
          </button>
          <!-- Samsung Internet: не ставимо самі, а перекидаємо в Chrome -->
          <button v-if="isSamsungUA" class="pwa-btn pwa-btn-primary" @click="openChrome">
            {{ t('pwa.install.openInChrome') }}
          </button>
          <!-- Android/Desktop -->
          <button v-if="!isIOSUA && !isSamsungUA && deferredPrompt" class="pwa-btn pwa-btn-primary" @click="installApp">
            {{ t('pwa.install.button') }}
          </button>
          <button class="pwa-btn pwa-btn-secondary" @click="dismissSoft">
            {{ t('pwa.later') }}
          </button>
          <button class="pwa-btn pwa-btn-ghost" @click="dismissHard">
            {{ t('pwa.install.dontShow') }}
          </button>
        </div>
        <button class="pwa-install-close" @click="dismissSoft" :aria-label="t('common.close')">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { t } from '../services/i18nService'
import { useIosInstallGuide, isIOS, isSafariOnIOS, isStandalone } from '../composables/useIosInstallGuide'
import { usePwaInstall, isSamsungInternet, openInChrome } from '../composables/usePwaInstall'

// Ключі localStorage
const SOFT_DISMISS_KEY = 'pwa-install-soft-dismissed'  // "пізніше" — повторити через кілька днів
const HARD_DISMISS_KEY = 'pwa-install-hard-dismissed'  // "не показувати" — довга пауза
const SHOWN_COUNT_KEY = 'pwa-install-shown-count'

// Інтервали повторного показу
const SOFT_DISMISS_DURATION = 3 * 24 * 60 * 60 * 1000   // 3 дні після "Пізніше"
const HARD_DISMISS_DURATION = 60 * 24 * 60 * 60 * 1000  // 60 днів після "Не показувати"

const { openGuide } = useIosInstallGuide()
const { deferredPrompt, triggerNativeInstall, installed } = usePwaInstall()

const showPrompt = ref(false)
const copied = ref(false)

const isIOSUA = computed(() => isIOS())
const isSafari = computed(() => isSafariOnIOS())
const isSamsungUA = computed(() => isSamsungInternet())

const isMobileDevice = computed(() => {
  if (typeof window === 'undefined') return false
  const hasMobileUA = /android|iphone|ipad|ipod|mobile|tablet/i.test(window.navigator.userAgent)
  const isSmallScreen = window.innerWidth <= 1024
  return hasMobileUA && isSmallScreen
})

// Чи був prompt відхилений (soft або hard) й чи не минув інтервал
const isDismissed = () => {
  try {
    const hard = parseInt(localStorage.getItem(HARD_DISMISS_KEY) || '0', 10)
    if (hard && Date.now() - hard < HARD_DISMISS_DURATION) return true
    const soft = parseInt(localStorage.getItem(SOFT_DISMISS_KEY) || '0', 10)
    if (soft && Date.now() - soft < SOFT_DISMISS_DURATION) return true
    return false
  } catch {
    return false
  }
}

const markSoft = () => {
  try { localStorage.setItem(SOFT_DISMISS_KEY, String(Date.now())) } catch {}
}
const markHard = () => {
  try { localStorage.setItem(HARD_DISMISS_KEY, String(Date.now())) } catch {}
}
const incrementShownCount = () => {
  try {
    const n = parseInt(localStorage.getItem(SHOWN_COUNT_KEY) || '0', 10) + 1
    localStorage.setItem(SHOWN_COUNT_KEY, String(n))
  } catch {}
}

const shouldShowPrompt = () => {
  if (isStandalone()) return false
  if (isDismissed()) return false
  if (!isMobileDevice.value) return false
  if (isIOSUA.value) return true   // iOS Safari — показуємо гайд, iOS не-Safari — просимо переключитись
  return true                       // Android
}

const dismissSoft = () => {
  showPrompt.value = false
  markSoft()
}
const dismissHard = () => {
  showPrompt.value = false
  markHard()
}
// При кліку на "Показати інструкцію" — ховаємо баннер як soft (повториться через 3 дні, якщо не встановив)
const showGuideAndDismissSoft = () => {
  openGuide(false)
  showPrompt.value = false
  markSoft()
}

// Копіювання URL для iOS-браузерів без Safari
const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
    copied.value = true
    setTimeout(() => { copied.value = false }, 1800)
  } catch {
    // fallback: selection через temp textarea
    const ta = document.createElement('textarea')
    ta.value = window.location.href
    document.body.appendChild(ta)
    ta.select()
    try { document.execCommand('copy'); copied.value = true } catch {}
    document.body.removeChild(ta)
    setTimeout(() => { copied.value = false }, 1800)
  }
}

const installApp = async () => {
  const { outcome } = await triggerNativeInstall()
  if (outcome === 'accepted') showPrompt.value = false
}

// Samsung Internet: гасимо баннер як soft — якщо юзер повернеться сюди,
// не нагадуємо ще 3 дні, установка тепер має відбутися в Chrome
const openChrome = () => {
  showPrompt.value = false
  markSoft()
  openInChrome()
}

// Android/Desktop Chrome: коли прилетає beforeinstallprompt — показуємо баннер.
// Samsung Internet теж шле цю подію, але його баннер піднімаємо в onMounted —
// тут він призвів би до подвійного показу й подвійного інкремента лічильника.
watch(deferredPrompt, (val) => {
  if (val && !isSamsungUA.value && shouldShowPrompt()) {
    showPrompt.value = true
    incrementShownCount()
  }
})

// Після встановлення ховаємо баннер
watch(installed, (val) => {
  if (val) showPrompt.value = false
})

onMounted(() => {
  // iOS та Samsung Internet: баннер не залежить від beforeinstallprompt
  // (нативну установку в обох випадках не запускаємо) — показуємо із затримкою
  if ((isIOSUA.value || isSamsungUA.value) && shouldShowPrompt()) {
    setTimeout(() => {
      showPrompt.value = true
      incrementShownCount()
    }, 3000)
  }
  // Android/Chrome: якщо подія beforeinstallprompt вже прилетіла до монтування
  if (!isIOSUA.value && !isSamsungUA.value && deferredPrompt.value && shouldShowPrompt()) {
    showPrompt.value = true
    incrementShownCount()
  }
})
</script>

<style scoped>
.pwa-install-prompt {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10001;
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%);
  box-shadow: var(--shadow-md);
}

.pwa-install-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}

.pwa-install-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pwa-install-icon .material-symbols-outlined {
  font-size: 24px;
  color: var(--text-inverse);
}

.pwa-install-text { flex: 1; min-width: 0; }
.pwa-install-title {
  color: var(--text-inverse);
  font-size: var(--text-base);
  font-weight: 600;
  margin: 0 0 4px 0;
}
.pwa-install-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: var(--text-sm);
  margin: 0;
}

.pwa-install-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  margin-top: 4px;
}

.pwa-btn {
  padding: 7px 14px;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.pwa-btn-secondary { background: rgba(255, 255, 255, 0.2); color: var(--text-inverse); }
.pwa-btn-secondary:hover { background: rgba(255, 255, 255, 0.3); }
.pwa-btn-primary { background: var(--bg-card); color: var(--primary); }
.pwa-btn-primary:hover { background: var(--bg-secondary); }
.pwa-btn-ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.75rem;
  padding: 4px 8px;
  text-decoration: underline;
  font-weight: 400;
}
.pwa-btn-ghost:hover { color: var(--text-inverse); }

.pwa-install-close {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.pwa-install-close:hover { background: rgba(0, 0, 0, 0.3); }
.pwa-install-close .material-symbols-outlined { font-size: 18px; color: var(--text-inverse); }

.slide-up-enter-active,
.slide-up-leave-active { transition: transform 0.3s ease, opacity 0.3s ease; }
.slide-up-enter-from,
.slide-up-leave-to { transform: translateY(100%); opacity: 0; }

@media (max-width: 480px) {
  .pwa-install-content { flex-wrap: wrap; }
  .pwa-install-icon { display: none; }
  .pwa-install-text { flex: 1 1 100%; padding-right: 32px; }
  .pwa-install-actions { flex: 1 1 100%; flex-direction: row; flex-wrap: wrap; justify-content: flex-end; margin-top: 12px; }
}
</style>
